import styled, { css, RuleSet } from 'styled-components';
import {
  ellipsisText,
  FlexCenterAligned,
  FlexColumn
} from '../../../styled/common';
import { Typography } from '../Typography';
import { SelectContainerProps, SelectHorizontalAlignmentEnum } from './types';
import { StyledDeleteIcon } from '../Input/styled';
import { ThemeType } from '../../../styled/theme/constants';
import { getSafeAreaValue } from '../../../styled/theme/helpers/getSafeAreaValue';
import { Breakpoints } from '../../../styled/constants';

export const selectHorizontalAlignment = (
  horizontalAlign: SelectHorizontalAlignmentEnum
) => css`
  top: calc(100% + 6px);
  ${horizontalAlign === SelectHorizontalAlignmentEnum.Left &&
  css`
    left: 0;
  `};
  ${horizontalAlign === SelectHorizontalAlignmentEnum.Right &&
  css`
    right: -1px;
  `};
`;

export const SelectText = styled(Typography)<{
  $isError: boolean;
  $hasValue?: boolean;
}>`
  color: ${({ theme, $hasValue }) =>
    theme.colors.text[
      $hasValue || theme.mode === ThemeType.dark ? 'primary' : 'secondary'
    ]};
  text-align: left;
  ${ellipsisText};

  ${({ $isError }) =>
    $isError &&
    css`
      color: ${({ theme }) => theme.colors.systemColors.red};
    `}
`;

const selectedStyles = css`
  path:not(${StyledDeleteIcon} path) {
    fill: ${({ theme }) => theme.colors.systemColors.blue};
    color: ${({ theme }) => theme.colors.systemColors.blue};
    stroke: ${({ theme }) => theme.colors.systemColors.blue};
  }

  ${SelectText} {
    color: ${({ theme }) => theme.colors.systemColors.blue};
  }
`;

export const SelectContainer = styled.button<SelectContainerProps>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  gap: 5px;
  height: 54px;
  width: max-content;
  justify-content: space-between;
  box-sizing: border-box;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    fill 0.3s ease-in-out,
    color 0.3s ease-in-out,
    stroke 0.3s ease-in-out;

  svg path {
    fill: ${({ theme, $hasValue }) =>
      $hasValue || theme.mode === ThemeType.dark
        ? theme.colors.text.primary
        : theme.colors.text.secondary};
  }

  ${({ theme, $hasValue }) =>
    $hasValue && theme.mode === ThemeType.dark
      ? css`
          border: 1px solid rgba(254, 254, 254, 0.08);
          background: rgba(254, 254, 254, 0.15);
        `
      : ''};

  ${({ $isWithHover, $disabled }) =>
    $isWithHover &&
    !$disabled &&
    css`
      @media (hover: hover) and (pointer: fine) {
        &:hover {
          ${selectedStyles};
        }
      }
    `}

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      @media (min-width: ${Breakpoints.smallDesktop}px) {
        ${selectedStyles}
      }
    `}

  ${({ $styles }) => $styles};

  ${({ $isError }) =>
    $isError &&
    css`
      border: 1px solid ${({ theme }) => theme.colors.systemColors.red};
      background-color: ${({ theme }) => theme.colors.background.primary};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-color: ${({ theme }) => theme.colors.background.tertiary};
      cursor: unset;

      ${SelectText} {
        color: ${({ theme }) => theme.colors.text.tertiary};

        svg path {
          stroke: ${({ theme }) => theme.colors.text.tertiary};
        }
      }
    `}
`;

export const TextsContainer = styled(FlexColumn)`
  gap: 3px;
  width: 78%;
`;

export const SelectLabel = styled(Typography)<{ $isError: boolean }>`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: left;

  ${({ $isError }) =>
    $isError &&
    css`
      color: ${({ theme }) => theme.colors.systemColors.red};
    `}
`;

export const ArrowIcon = styled(FlexCenterAligned)<
  Pick<SelectContainerProps, '$isSelected' | '$disabled'>
>`
  width: 18px;
  height: 18px;
  transform: rotate(90deg);

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      transform: rotate(270deg);
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      svg path {
        fill: ${({ theme }) => theme.colors.text.tertiary};
      }
    `}
`;

export const modalStyles = (styles?: RuleSet<object>) => css`
  border-radius: 7px 7px 0 0;
  padding-bottom: env(safe-area-inset-bottom);
  height: max-content;
  align-self: end;

  ${styles};
`;

export const ModalContent = styled.div`
  height: 100%;
  padding: 15px 15px ${({ theme }) => getSafeAreaValue(theme, 'bottom', 25)};
  overflow-y: scroll;
  background-color: ${({ theme: { colors } }) => colors.background.primary};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
`;
